import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlacesInput = ({ value, onChange, placeholder, label, required, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input) => {
    if (input.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/places-autocomplete`, { params: { input } });
      setSuggestions(res.data.predictions || []);
      setShowDropdown(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (description) => {
    onChange(description);
    setShowDropdown(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm text-[#A1A1AA] mb-2">
        <MapPin className="w-4 h-4 inline mr-1 text-[#D4AF37]" />
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
          placeholder={placeholder}
          required={required}
          data-testid={testId}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
          </div>
        )}
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#D4AF37]/30 max-h-48 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={s.place_id || i}
              type="button"
              onClick={() => handleSelect(s.description)}
              className="w-full text-left px-4 py-3 text-sm text-[#A1A1AA] hover:bg-[#D4AF37]/10 hover:text-white transition-colors border-b border-white/5 last:border-0"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <MapPin className="w-3 h-3 inline mr-2 text-[#D4AF37]" />
              {s.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlacesInput;
